import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { tap } from "rxjs/operators";
import { RbacService } from "../onboarding-users-roles/rbac.service";
import { SecurityService } from "./security.service";

@Injectable({
  providedIn: "root",
})
export class UserPermissionsService {
  private readonly storageKey = "onboardingUserPermissions";
  private permissionsResponse: any | null = null;
  private allowedMenuNames = new Set<string>();
  private permissionNames = new Set<string>();
  private loaded = false;

  constructor(
    private rbacService: RbacService,
    private securityService: SecurityService
  ) {}

  loadCurrentUserPermissions(forceRefresh = false): Observable<any> {
    if (this.permissionsResponse && !forceRefresh) {
      return of(this.permissionsResponse);
    }

    const partnerId = this.getPartnerId();
    const userId = this.securityService.getTokenAdminOrUser();

    if (!partnerId || !userId) {
      console.warn("[PERMISSIONS] No se pueden cargar permisos: falta partnerId o userId.", {
        partnerId,
        userId,
      });
      return of(null);
    }

    return this.rbacService.getUserPermissions(partnerId, userId).pipe(
      tap((response) => {
        this.setPermissionsResponse(response);
        console.log("[PERMISSIONS] Permisos efectivos del usuario:", response);
      })
    );
  }

  getCachedPermissionsResponse(): any | null {
    if (this.permissionsResponse) {
      return this.permissionsResponse;
    }

    const cached = localStorage.getItem(this.storageKey);
    if (!cached) {
      return null;
    }

    try {
      this.setPermissionsResponse(JSON.parse(cached));
      return this.permissionsResponse;
    } catch {
      localStorage.removeItem(this.storageKey);
      return null;
    }
  }

  hasMenu(menuName: string): boolean {
    if (!this.loaded) {
      this.getCachedPermissionsResponse();
    }

    return !this.loaded || this.allowedMenuNames.has(menuName);
  }

  hasPermission(permissionName: string): boolean {
    if (!this.loaded) {
      this.getCachedPermissionsResponse();
    }

    return !this.loaded || this.permissionNames.has(permissionName);
  }

  hasAnyPermission(permissionNames: string[]): boolean {
    return permissionNames.some((permissionName) => this.hasPermission(permissionName));
  }

  private setPermissionsResponse(response: any): void {
    this.permissionsResponse = response;
    this.allowedMenuNames = new Set<string>();
    this.permissionNames = new Set<string>();

    const menus = this.extractMenus(response);
    menus.forEach((menu: any) => {
      if (menu?.name) {
        this.allowedMenuNames.add(menu.name);
      }

      (menu?.permissions || []).forEach((permission: any) => {
        if (permission?.name) {
          this.permissionNames.add(permission.name);
        }
      });
    });

    this.loaded = true;
    localStorage.setItem(this.storageKey, JSON.stringify(response));
  }

  private extractMenus(response: any): any[] {
    if (Array.isArray(response?.data?.menus)) return response.data.menus;
    if (Array.isArray(response?.result?.data?.menus)) return response.result.data.menus;
    if (Array.isArray(response?.menus)) return response.menus;
    return [];
  }

  private getPartnerId(): number | null {
    const partnerId = this.securityService.getTokenPartner();
    return partnerId ? Number(partnerId) : null;
  }
}
